import {
  AccessibleContent,
  Button,
  Flex,
  Heading,
  IconAddLine,
  InlineList,
  RadioInput,
  RadioInputGroup,
  ScreenReaderContent,
  SimpleSelect,
  Tag,
  Text,
  TextInput,
} from "@instructure/ui";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApDataContext } from "../../../../views/Plan";
import RichTextEditor, {
  rteHasContent,
} from "../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../RichTextArea/RichTextViewer";
import { GoalsSingleSelect } from "../../../Selectors/GoalSelect";
import { ObjectiveSelect } from "../../../Selectors/ObjectiveSelect";
import { emailIsInList, emailVerification } from "../utils";

const QualitativeMeasureForm = ({
  goalSelectHandler,
  selectedCycle,
  cycles,
  selectedGoal,
  clear,
  objectiveSelectHandler,
  selectedObjective,
  trigger,
  setTrigger,
  measureNameRef,
  uploadEvidence,
  setUploadEvidence,
  measureName,
  qualEmails,
  setQualEmails,
  instructions,
  setInstructions,
  inTemplate,
}) => {
  const { t } = useTranslation();
  const { extraData } = useContext(ApDataContext);
  const emailRef = useRef();
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  useEffect(() => {
    if (measureNameRef.current) {
      measureNameRef.current.value = measureName;
    }
  }, [measureName, measureNameRef]);

  const handleAddAssignee = () => {
    // Checks to see if the email is valid
    if (!emailVerification(emailRef.current.value)) {
      setInvalidEmail(true);
      setDuplicateEmail(false);
    } else if (emailIsInList(qualEmails, emailRef.current.value)) {
      setDuplicateEmail(true);
      setInvalidEmail(false);
    } else {
      setInvalidEmail(false);
      setDuplicateEmail(false);
      setQualEmails((prevEmails) => [...prevEmails, emailRef.current.value]);
      emailRef.current.value = "";
    }
  };

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="50%">
        <TextInput
          renderLabel={<Text size="small">{t("measures.name")}</Text>}
          size="small"
          inputRef={(el) => (measureNameRef.current = el)}
          interaction={inTemplate ? "disabled" : "enabled"}
        />
      </Flex.Item>
      <Flex.Item>
        <RadioInputGroup
          name="qm-evidence"
          value={uploadEvidence}
          description={
            <Text size="small">{t("measures.uploadEvidence")}?</Text>
          }
          layout="columns"
          onChange={(e) => setUploadEvidence(e.target.value)}
          disabled={inTemplate}
        >
          <RadioInput value="yes" label={t("dialogYesNo.yes")} />
          <RadioInput value="no" label={t("dialogYesNo.no")} />
        </RadioInputGroup>
      </Flex.Item>
      {!inTemplate && (
        <Flex.Item size="100%">
          <RichTextEditor
            id="measure-instructions"
            label={
              <Text weight="bold" size="small">
                {t("assessment.instructions")}:
              </Text>
            }
            defaultValue={instructions}
            onChange={(newValue) => setInstructions(newValue)}
          />
        </Flex.Item>
      )}
      {inTemplate && rteHasContent(instructions) && (
        <Flex.Item size="100%">
          <RichTextViewer
            id="measure-instructions"
            label={
              <Text weight="bold" size="small">
                {t("assessment.instructions")}:
              </Text>
            }
            value={instructions}
          />
        </Flex.Item>
      )}
      <Flex.Item size="100%">
        <Heading as="h3" level="h5" border="bottom" margin="0 0 small 0">
          {t("assessment.with")}
        </Heading>
        <Flex gap="small" wrap="wrap">
          <Flex.Item shouldGrow shouldShrink>
            <GoalsSingleSelect
              selectHandler={goalSelectHandler}
              goalsList={cycles.find((c) => c.id === selectedCycle.id).goals}
              selectedGoal={selectedGoal}
              size="small"
              clear={clear}
              isRequired
              interaction={!extraData?.plan?.from_template}
            />
          </Flex.Item>
          <Flex.Item shouldGrow shouldShrink>
            {extraData?.plan?.from_template ? (
              // Hacky way of showing disabled objective. Selector component needs refactor
              <SimpleSelect
                renderLabel={
                  <Text size="small">{t("assessment.objective")}</Text>
                }
                size="small"
                interaction="disabled"
              >
                <SimpleSelect.Option id="0" value="0">
                  {selectedCycle.goals
                    .find((g) => g.id === selectedGoal)
                    .objectives.find((o) => o.id === selectedObjective)
                    ?.description ?? "None"}
                </SimpleSelect.Option>
              </SimpleSelect>
            ) : (
              <ObjectiveSelect
                selectHandler={objectiveSelectHandler}
                selectedGoal={selectedGoal}
                selectedCycle={selectedCycle}
                cycles={cycles}
                selectedObjective={selectedObjective}
                trigger={trigger || clear}
                setTrigger={setTrigger}
                size="small"
              />
            )}
          </Flex.Item>
        </Flex>
      </Flex.Item>
      <Flex.Item size="100%">
        <Heading as="h3" level="h5" border="bottom" margin="0 0 small 0">
          {t("measures.assignedTo")}
        </Heading>
        <Flex gap="small">
          <Flex.Item size="25%">
            <TextInput
              renderLabel={
                <ScreenReaderContent>
                  {t("assessment.email")}
                </ScreenReaderContent>
              }
              placeholder={t("assessment.email")}
              size="small"
              inputRef={(el) => (emailRef.current = el)}
              messages={
                invalidEmail
                  ? [{ text: t("assessment.invalidEmail"), type: "error" }]
                  : duplicateEmail
                    ? [
                        {
                          text: t("assessment.emailAlreadyAdded"),
                          type: "error",
                        },
                      ]
                    : []
              }
            />
          </Flex.Item>
          <Flex.Item align="start">
            <Button
              size="small"
              renderIcon={IconAddLine}
              onClick={handleAddAssignee}
            >
              {t("global.add")}
            </Button>
          </Flex.Item>
        </Flex>
      </Flex.Item>
      {qualEmails.length > 0 && (
        <Flex.Item size="100%">
          <InlineList>
            {qualEmails.map((email, key) => (
              <InlineList.Item key={key}>
                <Tag
                  text={
                    <AccessibleContent alt={t("assessment.deleteAssessor")}>
                      {email}
                    </AccessibleContent>
                  }
                  dismissible
                  onClick={() => {
                    setQualEmails((prevEmails) =>
                      prevEmails.filter((_e, index) => index !== key)
                    );
                  }}
                />
              </InlineList.Item>
            ))}
          </InlineList>
        </Flex.Item>
      )}
    </Flex>
  );
};

export default QualitativeMeasureForm;
